import { t } from 'i18next';
import { useEffect, useState } from 'react';
import { ToastType } from 'react-toastify';

import type { Targetable } from '@feathr/blackbox';
import { toast } from '@feathr/components';
import { useStore } from '@feathr/extender/state';
import { useDebouncedCallback } from '@feathr/hooks';

export function useSearch(): {
  isSearching: boolean;
  filteredInterests: Targetable[];
  allInterests: Targetable[];
  categories: string[];
  selectedCategory: string;
  handleSearch: (value?: string) => void;
  handleCategoryChange: (category: string) => void;
  search: string;
  isLoading: boolean;
} {
  const { Targetables } = useStore();

  const [search, setSearch] = useState<string>('');
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [filteredInterests, setFilteredInterests] = useState<Targetable[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('suggested');
  const [allInterests, setAllInterests] = useState<Targetable[]>([]);

  // Initialize with all interests and categories
  useEffect(() => {
    const loadInterests = async (): Promise<void> => {
      setIsLoading(true);

      try {
        const result = await Targetables.getInterests();
        setAllInterests(result.targetables);
        setFilteredInterests(result.targetables);
        setCategories(result.categories);
      } catch (error) {
        // Failed to load interests
        setAllInterests([]);
        setFilteredInterests([]);
        setCategories([]);
        toast(t('Failed to load interests'), {
          type: ToastType.ERROR,
        });
      } finally {
        setIsLoading(false);
      }
    };
    loadInterests();
  }, [Targetables]);

  // Filter interests based on selected category
  useEffect(() => {
    if (selectedCategory === 'suggested') {
      // Filter interests that have suggested = true
      const suggestedInterests = allInterests.filter((interest) => {
        const interestData = interest.get('interest');
        return interestData && interestData.is_popular === true;
      });

      // If no suggested interests, show first 5 interests as fallback
      if (suggestedInterests.length === 0) {
        setFilteredInterests(allInterests.slice(0, 5));
      } else {
        setFilteredInterests(suggestedInterests);
      }
    } else {
      // Filter by category - use the nested interest.category_name field
      const categoryInterests = allInterests.filter((interest) => {
        const interestData = interest.get('interest');
        return interestData && interestData.category_name === selectedCategory;
      });
      setFilteredInterests(categoryInterests);
    }
  }, [selectedCategory, allInterests]);

  // Debounced search function that filters locally
  const debouncedSearch = useDebouncedCallback(
    async (searchTerm: string) => {
      if (!searchTerm || searchTerm.trim().length < 2) {
        // If search is empty or too short, show interests based on selected category
        if (selectedCategory === 'suggested') {
          const suggestedInterests = allInterests.filter((interest) => {
            const interestData = interest.get('interest');
            return interestData && interestData.is_popular === true;
          });

          if (suggestedInterests.length === 0) {
            setFilteredInterests(allInterests.slice(0, 5));
          } else {
            setFilteredInterests(suggestedInterests);
          }
        } else {
          // Filter by category - use the nested interest.category_name field
          const categoryInterests = allInterests.filter((interest) => {
            const interestData = interest.get('interest');
            return interestData && interestData.category_name === selectedCategory;
          });
          setFilteredInterests(categoryInterests);
        }
        return;
      }

      setIsSearching(true);
      try {
        // Filter interests locally based on search term and current category
        let searchableInterests = allInterests;

        // First filter by category if not "Suggested"
        if (selectedCategory !== 'suggested') {
          searchableInterests = allInterests.filter(
            (interest) => interest.get('category_name') === selectedCategory,
          );
        }

        // Then filter by search term
        const searchResults = searchableInterests.filter((interest) => {
          const name = interest.get('name')?.toLowerCase() || '';
          const description = interest.get('interest')?.description?.toLowerCase() || '';
          const category = interest.get('category_name')?.toLowerCase() || '';
          const searchLower = searchTerm.toLowerCase();

          return (
            name.includes(searchLower) ||
            description.includes(searchLower) ||
            category.includes(searchLower)
          );
        });

        setFilteredInterests(searchResults);
      } catch (error) {
        setFilteredInterests([]);
      } finally {
        setIsSearching(false);
      }
    },
    500,
    [],
  );

  function handleCategoryChange(category: string): void {
    setSelectedCategory(category);
  }

  function handleSearch(value?: string): void {
    setSearch(value || '');

    if (!value || value.trim().length < 2) {
      // If search is empty or too short, show interests based on selected category immediately
      if (selectedCategory === 'suggested') {
        const suggestedInterests = allInterests.filter((interest) => {
          const interestData = interest.get('interest');
          return interestData && interestData.is_popular === true;
        });

        if (suggestedInterests.length === 0) {
          setFilteredInterests(allInterests.slice(0, 5));
        } else {
          setFilteredInterests(suggestedInterests);
        }
      } else {
        const categoryInterests = allInterests.filter(
          (interest) => interest.get('category_name') === selectedCategory,
        );
        setFilteredInterests(categoryInterests);
      }
      setIsSearching(false);
    } else {
      // Set loading state immediately for better UX
      setIsSearching(true);
      debouncedSearch(value);
    }
  }

  return {
    isSearching,
    filteredInterests,
    allInterests,
    categories,
    selectedCategory,
    handleSearch,
    handleCategoryChange,
    search,
    isLoading,
  };
}

export default useSearch;
